// Works as an extension to DxfLineParser and generated arcs will be added to the LineSegments generated by DxfLineParser
import * as THREE from 'three';
import * as mathHelpers from '../../../utilities/math-helpers';
import { getNumberOfSteps } from './get-arc-vertices.ts';

export default class DxfArcParser {
  constructor() {}

  getCircleVertices(entity) {
    let circleVertices = [];

    const angleLength = 2 * Math.PI;
    const segmentCount = getNumberOfSteps(entity.radius, angleLength);

    for (let i = 0; i <= segmentCount; i++) {
      let theta = (i / segmentCount) * Math.PI * 2;
      circleVertices.push({
        x: Math.cos(theta) * entity.radius + entity.center.x,
        y: Math.sin(theta) * entity.radius + entity.center.y,
        z: entity.center.z,
      });
    }

    return circleVertices;
  }

  getSplineVertices(entity) {
    let vertices = [];
    entity.controlPoints.map((point) => {
      vertices.push(new THREE.Vector2(point.x, point.y));
    });

    let spline = new THREE.SplineCurve(vertices);

    let length = spline.getLength();
    let points = spline.getSpacedPoints(
      helpers.getSpacingValue(length < vertices.length ? 3 : length / 2),
    );

    let splineVertices = points.map((point) => {
      point.z = entity.controlPoints[0].z;
      return { x: point.x, y: point.y, z: point.z };
    });

    return splineVertices;
  }

  getEllipseVertices(entity) {
    let p1 = helpers.translatePoint(
      entity.center,
      mathHelpers.radiansToDegrees(entity.startAngle),
      10,
    );
    let p2 = helpers.translatePoint(
      entity.center,
      mathHelpers.radiansToDegrees(entity.endAngle),
      10,
    );

    let rotation = helpers.getAngle(entity.majorAxisEndPoint) - Math.PI / 2;

    let majorRadius = helpers.getMagnitude(entity.majorAxisEndPoint);
    let minorRadius = majorRadius * entity.axisRatio;

    let arc = new THREE.EllipseCurve(
      entity.center.x,
      entity.center.y, // ax, aY
      minorRadius,
      majorRadius, // xRadius, yRadius
      entity.startAngle + Math.PI / 2,
      entity.endAngle + Math.PI / 2, // aStartAngle, aEndAngle
      helpers.isClockWise(p1, entity.center, p2), // aClockwise
    );

    arc.aRotation = rotation;

    let points = arc.getSpacedPoints(helpers.getSpacingValue((majorRadius + minorRadius) / 3.2));
    let ellipseVertices = points.map((point) => {
      point.z = entity.center.z;
      return { x: point.x, y: point.y, z: point.z };
    });

    return ellipseVertices;
  }
}

export const helpers = {
  /** Get the midpoint between 2 points */
  getCenter(p1, p2) {
    var sum = this.addCoords(p1, p2);
    return { x: sum.x / 2, y: sum.y / 2, z: sum.z / 2 };
  },

  getSpacingValue(radius) {
    return radius < 1 ? 5 : radius * 5;
  },

  /** Gets the angle in degrees of a line based on 2 points (adjusted with -90 degrees to fit THREE Orientation) */
  getLineAngle(point1, point2) {
    var dx = point1.x - point2.x;
    var dy = point2.y - point1.y;
    return (mathHelpers.radiansToDegrees(Math.atan2(dy, dx)) - 90) % 360;
  },

  /** Moves a point with a specific angle (degrees) and magnitude */
  translatePoint(point, angle, unit) {
    var x = point.x;
    var y = point.y;
    var rad = mathHelpers.degreesToRadians(angle);

    x += unit * Math.sin(rad);
    y += unit * Math.cos(rad);

    return { x: x, y: y, z: point.z ? point.z : 0 };
  },

  /** Adds two coordinates to eachother */
  addCoords(original, offset) {
    var newCoords = {};
    newCoords.x = original.x + offset.x;
    newCoords.y = original.y + offset.y;
    newCoords.z = original.z + offset.z;

    if (!newCoords.z) {
      newCoords.z = 0;
    }

    return newCoords;
  },

  /** Subtracts coordinate 1 with coordinate 2 */
  subtractCoords(point1, point2) {
    var newCoords = {};
    newCoords.x = point1.x - point2.x;
    newCoords.y = point1.y - point2.y;
    newCoords.z = point1.z - point2.z;

    if (!newCoords.z) {
      newCoords.z = 0;
    }

    return newCoords;
  },

  /** Checks if 2 lines will intersect */
  checkLineIntersection(line1, line2) {
    // if the lines intersect, the result contains the x and y of the intersection (treating the lines as infinite) and booleans for whether line segment 1 or line segment 2 contain the point
    var denominator,
      a,
      b,
      numerator1,
      numerator2,
      result = {
        x: null,
        y: null,
        onLine1: false,
        onLine2: false,
      };
    denominator =
      (line2.end.y - line2.start.y) * (line1.end.x - line1.start.x) -
      (line2.end.x - line2.start.x) * (line1.end.y - line1.start.y);
    if (denominator === 0) {
      return result;
    }
    a = line1.start.y - line2.start.y;
    b = line1.start.x - line2.start.x;
    numerator1 = (line2.end.x - line2.start.x) * a - (line2.end.y - line2.start.y) * b;
    numerator2 = (line1.end.x - line1.start.x) * a - (line1.end.y - line1.start.y) * b;
    a = numerator1 / denominator;
    b = numerator2 / denominator;

    // if we cast these lines infinitely in both directions, they intersect here:
    result.x = line1.start.x + a * (line1.end.x - line1.start.x);
    result.y = line1.start.y + a * (line1.end.y - line1.start.y);

    result.z = line1.start.z ? line1.start.z : 0;

    // if line1 is a segment and line2 is infinite, they intersect if:
    if (a > 0 && a < 1) {
      result.onLine1 = true;
    }
    // if line2 is a segment and line1 is infinite, they intersect if:
    if (b > 0 && b < 1) {
      result.onLine2 = true;
    }
    // if line1 and line2 are segments, they intersect if both of the above are true
    return result;
  },

  /** Get the distance between 2 points */
  getDistance(p1, p2) {
    var dx = p1.x - p2.x;
    var dy = p1.y - p2.y;
    return Math.sqrt(dx * dx + dy * dy);
  },

  /** Get angle (in radians) from vector NOTE: It's not rotated 0 rad is right ->*/
  getAngle(vector) {
    return Math.atan2(vector.y, vector.x);
  },

  /** Gets the magnitude (hypotenuse) of a vector */
  getMagnitude(v) {
    return Math.sqrt(Math.pow(v.x, 2) + Math.pow(v.y, 2));
  },

  /** Checks if a set of 3 points are in clockwise order or not */
  isClockWise(p1, center, p2) {
    let sum = 0;

    sum += this.getCrossProduct(p1, center);
    sum += this.getCrossProduct(center, p2);
    sum += this.getCrossProduct(p2, p1);

    return sum >= 0;
  },

  /** Returns the cross product of two points */
  getCrossProduct(p1, p2) {
    return (p2.x - p1.x) * (p2.y + p1.y);
  },
};
